import { CourseModel, InstructorModel, PartnerModel, SessionModel, stringToSlug } from '@24hbs/utils';
import { CloudSearch } from '$lib/utils/cloud-search';
import { getCourseType, getLessonMode, mergeJSON } from '$lib/utils/filters/helpers';
import { FilterStore } from '$lib/utils/filters/store';
import type { IFilters } from '$lib/utils/filters/types';
import type { SearchFilterCurrentSearch } from '$lib/utils/searchfilter-store/types';
import { get } from 'svelte/store';
import { PUBLIC_SEARCH_ENGINE_BASE_URL } from '$env/static/public';
import { useCourseCategoryUtils } from '../courses/category-utils';
import type { ICourseCategory, ICourseCategoryFields, ICourseType } from '../contentful/types';

const searchDomain = PUBLIC_SEARCH_ENGINE_BASE_URL;

export interface SearchResults {
  courses: CourseModel[];
  instructors: InstructorModel[];
  partners: PartnerModel[];
  events: [];
  hasMore: boolean;
}

export class Search {
  public static DEFAULT_PAGE_SIZE = 50;

  private _boostFields: any;
  private _fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  }

  public constructor(fetch: {
    (input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
    (input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response>;
  }) {

    this._boostFields = {
      title: 5,
      product_code: 5,
      subtitle: 3,
      course_type_full: 3,
      category: 3,
      location: 3,
      competencies: 2,
      duration_detail: 2,
      lession_mode: 2,
      lession_time_detail: 2,
      certification: 1,
      on_sale_detail: 1,
      audience_content: 1,
      reasonwhy: 1,
      credit_detail: 1,
      session_contacts: 1,
      payment_detail: 1,
      admission_detail: 1,
      whoyouwill: 1,
      modules_list: 1,
      alternative_sessions: 3
      // duration_type: 1,
    };

    this._fetch = fetch;
  }

  private async postFetch(url: string, data: {
    [thingName: string]: any;
  } = {}): Promise<Response> {
    const formBody = [];
    for (const property in data) {
      const encodedKey = encodeURIComponent(property);
      const encodedValue = encodeURIComponent(data[property]);
      formBody.push(encodedKey + "=" + encodedValue);
    }
    const formData = formBody.join("&");
    return this._fetch(url.trim(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formData
    });
  }

  private getQueryOptions() {
    let qOptions = `{ fields:[`;
    for (let field in this._boostFields) {
      const weight = this._boostFields[field];
      qOptions += `'${field}^${weight}',`;
    }
    qOptions = qOptions.slice(0, -1);
    qOptions += ']}';
    return qOptions;
  }

  private formatSearchTerm(searchTerm: string): string {
    return searchTerm.replace(/'/g, "\\'");
  }

  private initSearchResultObject(): SearchResults {
    return <SearchResults>{
      courses: [],
      instructors: [],
      events: [],
      partners: [],
      hasMore: true,
    };
  }

  public profilerSearch = async (
    courseTypes: string[],
    category: string,
    lessionModes: string[],
    locations: string[] = []
  ): Promise<CourseModel[] | null> => {
    let data = {
      q: '',
      'q.parser': 'structured',
      size: 5,
    };

    data.q += "(and categories_slugs:'" + stringToSlug(category) + "' ";
    data.q += '(and (or';
    for (const courseType of courseTypes) {
      data.q += " course_type_full_slug:'" + stringToSlug(courseType) + "'";
    }
    data.q += ')';
    data.q += '(and (or';
    for (const lessionMode of lessionModes) {
      data.q += " lession_mode:'" + lessionMode + "'";
    }
    if (locations.length > 0) {
      for (const location of locations) {
        data.q += " location:'" + location + "'";
      }
    }
    data.q += '))))';

    return await this.postFetch(searchDomain, data)
      .then((res) => {
        return res.json();
      })
      .then((cloudSearchObject) => {
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          const searchResults: CourseModel[] = [];
          let validDataFound = false;

          for (const cloudSearchItemObject of cloudSearchObject.hits.hit) {
            if (!cloudSearchItemObject.fields) {
              continue;
            }
            if (cloudSearchItemObject.fields.model === 'SessionModel') {
              const session = CloudSearch.cloudSearchItemToSessionModel(cloudSearchItemObject);

              if (session !== null) {
                validDataFound = true;
                const course = new CourseModel();
                course.addSession(session);
                searchResults.push(course);
              }
            }
          }
          if (validDataFound) {
            return searchResults;
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
  };

  public getCourseBySlug = async (courseSlug: string): Promise<CourseModel | null> => {
    return await this._fetch(
      searchDomain + '?q.parser=structured&q=(and%20model:%27SessionModel%27%20 slug:%27' + courseSlug + '%27)&size=100'
    )
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          const course = new CourseModel();
          let validSessionFound = false;
          for (const cloudSearchItemObject of cloudSearchObject.hits.hit) {
            const session = CloudSearch.cloudSearchItemToSessionModel(cloudSearchItemObject);
            if (session != null) {
              validSessionFound = true;
              course.addSession(session);
            }
          }
          if (validSessionFound) {
            return course;
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
  };

  public getSessionByIdSession = async (idSession: string): Promise<SessionModel | null> => {
    const data = {
      q: `id_session:'${idSession}'`,
      'q.parser': 'structured',
      size: 100,
    };

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          return CloudSearch.cloudSearchItemToSessionModel(cloudSearchObject.hits.hit[0]);
        } else {
          return null;
        }
      });
  };

  public getSessionsByIdSessions = async (idSessions: string[]): Promise<SessionModel[]> => {
    if (!idSessions || !idSessions.length || idSessions.length <= 0) {
      return [];
    }
    const searchQuery = idSessions.reduce((text, value, i, array) => '_id:%27s' + text + '%27%20&size=100');

    return await this._fetch(searchDomain + '?q.parser=structured&q=(or%20' + searchQuery + ')')
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const sessions: SessionModel[] = [];
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          for (const cloudSearchItemObject of cloudSearchObject.hits.hit) {
            const session = CloudSearch.cloudSearchItemToSessionModel(cloudSearchItemObject);
            if (session !== null) {
              sessions.push(session);
            }
          }
        }
        return sessions;
      });
  };

  public getAllPartners = async (
    query: string,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE
  ): Promise<SearchResults | null> => {
    const start = page * pageSize;
    let queryUrl = '?';
    if (query && query.length > 0) {
      queryUrl += 'q=' + query;
    } else {
      queryUrl += 'q=matchall&q.parser=structured';
    }
    return await fetch(
      searchDomain + queryUrl + '&size=' + pageSize + '&fq=model:%27PartnerModel%27&start=' + start + '&sort=name%20asc'
    )
      .then((res) => {
        return res.json();
      })
      .then((cloudSearchObject) => {
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          const searchResults: SearchResults = {
            courses: [],
            instructors: [],
            events: [],
            partners: [],
            hasMore: true,
          };
          let validDataFound = false;

          if (cloudSearchObject.hits?.found <= pageSize * (page + 1)) {
            searchResults.hasMore = false;
          }
          for (const cloudSearchItemObject of cloudSearchObject.hits.hit) {
            if (!cloudSearchItemObject.fields) {
              continue;
            }
            if (cloudSearchItemObject.fields.model === 'PartnerModel') {
              const partner = CloudSearch.cloudSearchItemsToPartnerModel(cloudSearchItemObject);
              if (partner !== null) {
                validDataFound = true;
                searchResults.partners.push(partner);
              }
            }
          }
          if (validDataFound) {
            return searchResults;
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
  };

  public getPartnerByIdPartner = async (idPartner: number): Promise<PartnerModel | null> => {
    return await this._fetch(searchDomain + '?q.parser=structured&q=_id:%27p' + idPartner + '%27')
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          return CloudSearch.cloudSearchItemsToPartnerModel(cloudSearchObject.hits.hit[0]);
        } else {
          return null;
        }
      });
  };

  public getPartnersByIdPartners = async (idPartners: string[]): Promise<PartnerModel[]> => {
    if (!idPartners || !idPartners.length || idPartners.length <= 0) {
      return [];
    }
    const searchQuery = idPartners.reduce((text, value, i, array) => '_id:%27p' + text + '%27%20');

    return await this._fetch(searchDomain + '?q.parser=structured&q=(or%20' + searchQuery + ')')
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const partners: PartnerModel[] = [];
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          for (const cloudSearchItemObject of cloudSearchObject.hits.hit) {
            const partner = CloudSearch.cloudSearchItemsToPartnerModel(cloudSearchItemObject);
            if (partner !== null) {
              partners.push(partner);
            }
          }
        }
        return partners;
      });
  };

  public getAllInstructors = async (
    query: string,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE
  ): Promise<SearchResults | null> => {
    const start = page * pageSize;
    let queryUrl = '?';
    if (query && query.length > 0) {
      queryUrl += 'q=' + query;
    } else {
      queryUrl += 'q=matchall&q.parser=structured';
    }
    
    return await this._fetch(
      searchDomain +
      queryUrl +
      '&size=' +
      pageSize +
      '&fq=model:%27InstructorModel%27&start=' +
      start +
      '&sort=surname%20asc'
    )
      .then((res) => {
        return res.json();
      })
      .then((cloudSearchObject) => {
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          const searchResults: SearchResults = {
            courses: [],
            instructors: [],
            events: [],
            partners: [],
            hasMore: true,
          };
          let validDataFound = false;

          if (cloudSearchObject.hits?.found <= pageSize * (page + 1)) {
            searchResults.hasMore = false;
          }
          for (const cloudSearchItemObject of cloudSearchObject.hits.hit) {
            if (!cloudSearchItemObject.fields) {
              continue;
            }
            if (cloudSearchItemObject.fields.model === 'InstructorModel') {
              const instructor = CloudSearch.cloudSearchItemsToInstructorModel(cloudSearchItemObject);
              if (instructor !== null) {
                validDataFound = true;
                searchResults.instructors.push(instructor);
              }
            }
          }
          if (validDataFound) {
            return searchResults;
          } else {
            return null;
          }
        } else {
          return null;
        }
      });
  };

  public getInstructorByIdInstructor = async (idInstructor: string): Promise<InstructorModel | null> => {
    return await this._fetch(searchDomain + '?q.parser=structured&q=_id:%27i' + idInstructor + '%27')
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          return CloudSearch.cloudSearchItemsToInstructorModel(cloudSearchObject.hits.hit[0]);
        } else {
          return null;
        }
      });
  };

  public getInstructorsByIdInstructors = async (idInstructors: string[]): Promise<InstructorModel[]> => {
    if (!idInstructors || !idInstructors.length || idInstructors.length <= 0) {
      return [];
    }
    const searchQuery = idInstructors.reduce((text, value, i, array) => '_id:%27i' + text + '%27%20');

    return await this._fetch(searchDomain + '?q.parser=structured&q=(or%20' + searchQuery + ')')
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const instructors: InstructorModel[] = [];
        if (cloudSearchObject?.hits?.hit?.length && cloudSearchObject.hits.hit.length > 0) {
          for (const cloudSearchItemObject of cloudSearchObject.hits.hit) {
            const instructor = CloudSearch.cloudSearchItemsToInstructorModel(cloudSearchItemObject);
            if (instructor !== null) {
              instructors.push(instructor);
            }
          }
        }
        return instructors;
      });
  };

  /**
   * Get all facets
   */
  public async getFacets() {
    const filterStore = FilterStore.getInstance();
    let filters = get(filterStore.filterStore);

    const search = get(filterStore.searchStore);
    const hasSearch = search && search.length > 0;

    const query = CloudSearch.createQuery(
      {
        model: 'SessionModel',
        lessionMode: getLessonMode(filters.lessionMode ? filters.lessionMode : []),
        price: { min: filters.priceMin, max: filters.priceMax },
        courseType: getCourseType(filters.courseType ? filters.courseType : [], filters.studentType),
        month: filters.month,
        credits: filters.credits,
        categories: filters.category,
        certificationReleaseEntity: filters.certificationReleaseEntity,
        promoLabels: filters.promoLabels,
      },
      true
    );

    const data = {
      q: hasSearch ? `'${this.formatSearchTerm(search)}'` : query,
      'q.parser': !hasSearch ? 'structured' : 'simple',
      size: 0,
      'facet.course_type_full_slug': `{ sort: "bucket", size: 12} }`,
      'facet.categories_slugs': `{ sort: "bucket", size: 15} }`,
      'facet.lession_mode_slug': '{ sort: "bucket", size: 7 }',
      'facet.duration_slug': '{ sort: "bucket", size: 20 }',
      'facet.credits_cfu': `{  buckets: [1] }`,
      'facet.credits_cfp': `{  buckets: [1] }`,
      'facet.start_date': `{ buckets: [${CloudSearch.getMonthsFacetsQuery()}] }`,
      'facet.alternative_start_dates': `{ buckets: [${CloudSearch.getMonthsFacetsQuery()}] }`, // TODO: CHECK IT
      ...(hasSearch && { fq: query }),
      ...(hasSearch && { 'q.options': this.getQueryOptions() }),
    };
    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { facets } = CloudSearch.parseResponse(cloudSearchObject);
        filterStore.setAllFacets(facets);
      });
  }

  public getCoursesByCourseTypeAndSection(
    courseTypeSlug: string | string[],
    filters: IFilters,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE,
    section: 'A' | 'B' | null = null,
    excludedProductCodes: string[] = []
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const data = {
      q: 'matchall',
      'q.parser': 'structured',
      size: pageSize,
      start: page * pageSize,
      sort: 'rank_adjusted desc'
    };

    data.q = CloudSearch.createQuery(
      {
        model: 'SessionModel',
        courseType: courseTypeSlug,
        lessionMode: getLessonMode(filters.lessionMode ? filters.lessionMode : []),
        categories: filters.category,
        duration: filters.duration,
        month: filters.month,
        credits: filters.credits,
        price: { min: filters.priceMin, max: filters.priceMax },
        ...(section !== null && { rankSection: `SECTION_${section}` }),
        ...(excludedProductCodes.length > 0 && { excludedProductCodes }),
        certificationReleaseEntity: filters.certificationReleaseEntity,
      },
      true
    );

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }

  public getCoursesByCourseType(
    courseTypeSlug: string | string[],
    filters: IFilters,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE,
    sort: string = 'course_type_full_slug asc, start_date asc'
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const start = page * pageSize;
    sort = 'course_type_full_slug asc, start_date asc';

    if (!courseTypeSlug.includes('corsi-intensivi')) {
      sort = 'start_date asc';
    } else {
      sort = 'course_type_full_slug asc, start_date asc';
    }

    const data = {
      q: 'matchall',
      'q.parser': 'structured',
      size: pageSize,
      start: start,
      sort,
    };

    data.q = CloudSearch.createQuery(
      {
        model: 'SessionModel',
        courseType: courseTypeSlug,
        lessionMode: getLessonMode(filters.lessionMode ? filters.lessionMode : []),
        categories: filters.category,
        duration: filters.duration,
        month: filters.month,
        credits: filters.credits,
        certificationReleaseEntity: filters.certificationReleaseEntity,
        price: { min: filters.priceMin, max: filters.priceMax },
      },
      true
    );

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }

  public getCoursesByCourseTypeIn20Days(
    courseTypeSlug: string | string[]
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const start = 0;
    const pageSize = 1000;
    const data = {
      q: 'matchall',
      'q.parser': 'structured',
      size: pageSize,
      start: start,
      sort: 'start_date asc',
    };

    const today = new Date();
    const in20days = new Date(today);
    in20days.setDate(today.getDate() + 20);
    in20days.setUTCHours(23);
    in20days.setUTCMinutes(59);

    data.q = CloudSearch.createQuery({
      model: 'SessionModel',
      courseType: courseTypeSlug,
      date: {
        min: in20days.toISOString(),
      },
    });

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }

  public getCoursesByCourseTypeInRangeTime(
    courseTypeSlug: string | string[],
    dateStart: Date,
    dateEnd: Date
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const start = 0;
    const pageSize = 1000;
    const data = {
      q: 'matchall',
      'q.parser': 'structured',
      size: pageSize,
      start: start,
      sort: 'start_date asc',
    };

    dateEnd.setUTCHours(23);
    dateEnd.setUTCMinutes(59);

    data.q = CloudSearch.createQuery({
      model: 'SessionModel',
      courseType: courseTypeSlug,
      date: {
        min: dateStart.toISOString(),
        max: dateEnd.toISOString(),
      },
    });

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }

  public getCourses(
    searchTerm: string,
    filters: IFilters,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE,
    sort: string | null = null
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const start = page * pageSize;

    const query = CloudSearch.createQuery(
      {
        model: 'SessionModel',
        courseType: getCourseType(filters.courseType ? filters.courseType : [], filters.studentType),
        lessionMode: getLessonMode(filters.lessionMode ? filters.lessionMode : []),
        categories: filters.category,
        duration: filters.duration,
        month: filters.month,
        credits: filters.credits,
        certificationReleaseEntity: filters.certificationReleaseEntity,
        price: { min: filters.priceMin, max: filters.priceMax },
        promoLabels: filters.promoLabels,
      },
      true
    );
    const exprParts: string[] = [];

    // penalize online courses
    exprParts.push("-is_online_course*1000");

    if (!searchTerm) {
      sort = 'start_date asc';
    } else {
      exprParts.unshift('log10(price)*_score');
      sort = 'totalrank desc, start_date asc';
    }

    const expr = exprParts.length > 0 ? exprParts.join(' + ') : null;

    const data = {
      q: searchTerm ? `'${this.formatSearchTerm(searchTerm)}'` : query,
      'q.parser': searchTerm === '' ? 'structured' : 'simple',
      ...(searchTerm && { fq: query }),
      ...(expr && { 'expr.totalrank': expr }),
      size: pageSize,
      start: start,
      ...(sort && { sort: sort }),
      ...(searchTerm && { 'q.options': this.getQueryOptions() }),
    };

    // if (searchTerm) {
    //   data.q = `'${searchTerm}'`;
    //   data.fq = query;
    // }

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }

  public getPromoCoursesLandingPage(categoriesSlugs: string[]): Promise<CourseModel[]> {
    const query = CloudSearch.createQuery(
      {
        model: 'SessionModel',
        categories: categoriesSlugs,
        date: {
          min: new Date().toISOString(),
        },
        discount: {
          min: 1,
        },
      },
      true
    );

    const data = {
      q: query,
      'q.parser': 'structured',
      size: 3,
      start: 0,
      sort: 'start_date asc, price desc',
    };

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses } = CloudSearch.parseResponse(cloudSearchObject);
        return courses;
      });
  }

  public getCoursesByCategoriesSlugs(
    categoriesSlugs: string[],
    filters: IFilters,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE,
    sort: string = 'start_date asc'
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const start = page * pageSize;

    const query = CloudSearch.createQuery(
      {
        model: 'SessionModel',
        courseType: getCourseType(filters.courseType ? filters.courseType : [], filters.studentType),
        lessionMode: getLessonMode(filters.lessionMode ? filters.lessionMode : []),
        categories: categoriesSlugs,
        duration: filters.duration,
        price: { min: filters.priceMin, max: filters.priceMax },
        month: filters.month,
        credits: filters.credits,
        certificationReleaseEntity: filters.certificationReleaseEntity,
      },
      true
    );

    const data = {
      q: query,
      'q.parser': 'structured',
      size: pageSize,
      start: start,
      sort
    };

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }

  public getInstructors(
    searchTerm: string,
    filters: IFilters,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE
  ): Promise<{ instructors: InstructorModel[]; total: number }> {
    const instructors: InstructorModel[] = [];
    const start = page * pageSize;

    const query = CloudSearch.createQuery({
      model: 'InstructorModel',
      courseType: filters.courseType,
    });

    const data = {
      q: searchTerm ? `'${this.formatSearchTerm(searchTerm)}'` : query,
      'q.parser': 'structured',
      ...(searchTerm && { fq: query }),
      size: pageSize,
      start: start,
    };

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const hits = cloudSearchObject.hits;
        const total = hits.found;
        const dataArray = hits.hit;

        dataArray.forEach((element: any) => {
          if (element.fields) {
            const instructor: InstructorModel | null = CloudSearch.cloudSearchItemsToInstructorModel(element);

            if (instructor) {
              instructors.push(instructor);
            }
          }
        });
        return { instructors, total };
      });
  }

  public getCoursesByInstructorId(
    instructorId: string
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const start = 0;
    const pageSize = 1000;
    const data = {
      q: 'matchall',
      'q.parser': 'structured',
      size: pageSize,
      start: start,
      sort: 'start_date asc',
    };


    data.q = CloudSearch.createQuery({
      model: 'SessionModel',
      instructorId: instructorId,
    });

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }

  public getPartners(
    searchTerm: string,
    page: number = 0,
    pageSize: number = Search.DEFAULT_PAGE_SIZE
  ): Promise<{ partners: PartnerModel[]; total: number }> {
    const start = page * pageSize;
    const partners: PartnerModel[] = [];

    const query = CloudSearch.createQuery({
      model: 'PartnerModel',
    });

    const data = {
      q: searchTerm ? `'${this.formatSearchTerm(searchTerm)}'` : query,
      'q.parser': 'structured',
      ...(searchTerm && { fq: query }),
      size: pageSize,
      start: start,
    };

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const hits = cloudSearchObject.hits;
        const total = hits.found;
        const dataArray = hits.hit;

        dataArray.forEach((element: any) => {
          if (element.fields) {
            const partner: PartnerModel | null = CloudSearch.cloudSearchItemsToPartnerModel(element);

            if (partner) {
              partners.push(partner);
            }
          }
        });
        return { partners, total };
      });
  }

  public getCoursesByPartnerId(
    partnerId: string
  ): Promise<{ courses: CourseModel[]; total: number }> {
    const start = 0;
    const pageSize = 1000;
    const data = {
      q: 'matchall',
      'q.parser': 'structured',
      size: pageSize,
      start: start,
      sort: 'start_date asc',
    };

    data.q = CloudSearch.createQuery({
      model: 'SessionModel',
      partnerId: partnerId,
    });

    return this.postFetch(searchDomain, data)
      .then((res) => res.json())
      .then((cloudSearchObject) => {
        const { courses, total } = CloudSearch.parseResponse(cloudSearchObject);
        return { courses, total };
      });
  }
}
